import { Component, Input, ChangeDetectionStrategy, signal, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from '../../../../../shared/ui-kit/components/tooltip/tooltip.module';
import { XPlaceholderRowComponent } from '../../../../../shared/x-kit/components/x-placeholder-row/x-placeholder-row.component';
import { XPlaceholderFixedLoaderComponent } from '../../../../../shared/x-kit/components/x-placeholder-row/x-placeholder-fixed-loader/x-placeholder-fixed-loader.component';
import { XPlaceholderFlexibleLoaderComponent } from '../../../../../shared/x-kit/components/x-placeholder-row/x-placeholder-flexible-loader/x-placeholder-flexible-loader.component';

export type WidgetSize = 'compact' | 'medium' | 'full';

export interface FSVData {
  amount: string;
  amountRaw: number;
  haircutPercentage: number;
  haircutDescription: string;
  currency: string;
  policyLabel: string;
  mainText: string; // Основное предложение (bold)
  policyName: string; // Policy name для отображения с нового абзаца
}

@Component({
  selector: 'app-fsv-card',
  standalone: true,
  imports: [CommonModule, TranslateModule, TooltipModule, XPlaceholderRowComponent, XPlaceholderFixedLoaderComponent, XPlaceholderFlexibleLoaderComponent],
  template: `
    <div class="fsv-card" [class]="'size-' + currentSize()">
      <div class="card-header">
        <div class="title-block">
          <div class="title-text">
            <span class="card-title">{{ currentSize() === 'compact' ? ('fsv_widget_title_short' | translate) : ('fsv_widget_title' | translate) }}</span>
            <button class="info-button" type="button" [tooltip]="'fsv_widget_tooltip' | translate" [tooltipPosition]="'top'" [attr.aria-label]="'fsv_widget_aria_about' | translate" *ngIf="currentSize() !== 'compact'">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" stroke="#9CA3AF" stroke-width="1.5"/>
              <path d="M8 11V8M8 5h.01" stroke="#9CA3AF" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </div>
        <div class="toolbar-menu" #menuContainer>
          <button class="toolbar-button" type="button" #menuButton (click)="toggleMenu()" [attr.aria-label]="'fsv_widget_size_menu' | translate">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="3" r="1.5" fill="#9CA3AF"/>
              <circle cx="8" cy="8" r="1.5" fill="#9CA3AF"/>
              <circle cx="8" cy="13" r="1.5" fill="#9CA3AF"/>
            </svg>
          </button>
          <div class="size-menu" *ngIf="isMenuOpen()" #sizeMenu>
            <button class="size-option" [class.active]="currentSize() === 'compact'" (click)="setSize('compact')">
              <span class="size-icon">◼</span>
              <span>{{ 'fsv_widget_size_compact' | translate }}</span>
            </button>
            <button class="size-option" [class.active]="currentSize() === 'medium'" (click)="setSize('medium')">
              <span class="size-icon">▬</span>
              <span>{{ 'fsv_widget_size_medium' | translate }}</span>
            </button>
            <button class="size-option" [class.active]="currentSize() === 'full'" (click)="setSize('full')">
              <span class="size-icon">▮</span>
              <span>{{ 'fsv_widget_size_full' | translate }}</span>
            </button>
          </div>
        </div>
      </div>
      
      <!-- Skeleton Placeholder -->
      <div class="skeleton-placeholder" *ngIf="isLoading || !data">
        <app-x-placeholder-row>
          <app-x-placeholder-fixed-loader [width]="'100px'" [height]="'24px'"></app-x-placeholder-fixed-loader>
          <app-x-placeholder-flexible-loader [grow]="1"></app-x-placeholder-flexible-loader>
        </app-x-placeholder-row>
        
        <app-x-placeholder-row>
          <app-x-placeholder-fixed-loader [width]="'80px'" [height]="'20px'"></app-x-placeholder-fixed-loader>
          <app-x-placeholder-flexible-loader [grow]="1"></app-x-placeholder-flexible-loader>
        </app-x-placeholder-row>
        
        <app-x-placeholder-row>
          <app-x-placeholder-fixed-loader [width]="'60px'" [height]="'20px'"></app-x-placeholder-fixed-loader>
          <app-x-placeholder-flexible-loader [grow]="1"></app-x-placeholder-flexible-loader>
        </app-x-placeholder-row>
      </div>

      <!-- Content when loaded -->
      <ng-container *ngIf="!isLoading && data">
      <!-- COMPACT SIZE -->
      <div class="content-compact" *ngIf="currentSize() === 'compact'">
        <div class="value-number-compact">{{ formatToShort(data.amountRaw) }}</div>
        
        <div class="haircut-compact-block">
          <div class="haircut-compact-header">
            <span class="label">
              {{ 'fsv_widget_haircut_applied' | translate }}
              <button class="info-icon-button compact" type="button" [tooltip]="'fsv_widget_haircut_tooltip' | translate" [attr.aria-label]="'fsv_widget_haircut_aria' | translate">
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="7" stroke="#9CA3AF" stroke-width="1.5"/>
                  <path d="M8 11V8M8 5h.01" stroke="#9CA3AF" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
            </span>
            <span class="value">{{ data.haircutPercentage }}%</span>
          </div>
          <div class="haircut-track compact">
            <div class="haircut-fill" [style.width.%]="haircutWidth"></div>
          </div>
        </div>
      </div>
      
      <!-- MEDIUM & FULL SIZES -->
      <ng-container *ngIf="currentSize() !== 'compact'">
        <div class="value-section">
          <span class="value-number">{{ data.amount }}</span>
          <span class="value-currency" *ngIf="currentSize() === 'full'">{{ data.currency }}</span>
        </div>

        <div class="policy-row">
          <span class="policy-label">
            {{ 'fsv_widget_policy' | translate }}
              <button class="info-icon-button" type="button" [tooltip]="'fsv_widget_policy_tooltip' | translate" [tooltipPosition]="'top'" [attr.aria-label]="'fsv_widget_policy_aria' | translate">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="#9CA3AF" stroke-width="1.5"/>
                <path d="M8 11V8M8 5h.01" stroke="#9CA3AF" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </button>
          </span>
          <span class="policy-chip">{{ data.policyLabel }}</span>
        </div>

        <div class="haircut-section">
          <div class="haircut-header">
            <span class="haircut-label">
              {{ 'fsv_widget_haircut_applied' | translate }}
              <button class="info-icon-button" type="button" [tooltip]="'fsv_widget_haircut_tooltip' | translate" [tooltipPosition]="'top'" [attr.aria-label]="'fsv_widget_haircut_aria' | translate">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="7" stroke="#9CA3AF" stroke-width="1.5"/>
                  <path d="M8 11V8M8 5h.01" stroke="#9CA3AF" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
            </span>
            <span class="haircut-percentage">{{ data.haircutPercentage }}%</span>
          </div>
          <div class="haircut-track">
            <div class="haircut-fill" [style.width.%]="haircutWidth"></div>
        </div>
      </div>
      
        <div class="haircut-alert" *ngIf="currentSize() === 'full'">
          <div class="alert-icon">
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" stroke="#F97316" stroke-width="2"/>
              <path d="M10 5.5V11" stroke="#F97316" stroke-width="2" stroke-linecap="round"/>
              <circle cx="10" cy="14.5" r="1" fill="#F97316"/>
            </svg>
          </div>
          <div class="alert-text">
            <div class="alert-main-text">{{ data.mainText }}</div>
            <div class="alert-haircut">{{ 'fsv_widget_alert_haircut' | translate }}: {{ data.haircutPercentage }}%</div>
            <div class="alert-policy">{{ 'fsv_widget_alert_policy' | translate }}: {{ data.policyName }}</div>
      </div>
        </div>
      </ng-container>
      </ng-container>
    </div>
  `,
  styleUrl: './fsv-card.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FSVCardComponent implements OnDestroy {
  @Input() data: FSVData | null = null;
  @Input() isLoading: boolean = false;

  @ViewChild('menuContainer', { static: false }) menuContainer?: ElementRef;

  currentSize = signal<WidgetSize>('full');
  isMenuOpen = signal<boolean>(false);
  private clickOutsideHandler?: (event: MouseEvent) => void;

  constructor(private elementRef: ElementRef) {}

  ngOnDestroy() {
    this.removeClickOutsideListener();
  }

  private removeClickOutsideListener() {
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler, true);
      this.clickOutsideHandler = undefined;
    }
  }

  private addClickOutsideListener() {
    this.removeClickOutsideListener();
    
    this.clickOutsideHandler = (event: MouseEvent) => {
      if (!this.isMenuOpen() || !this.menuContainer) {
        return;
      }

      const target = event.target as Node;
      const menuElement = this.menuContainer.nativeElement;

      // Check if click is outside the menu container
      if (!menuElement.contains(target)) {
        this.isMenuOpen.set(false);
        this.removeClickOutsideListener();
      }
    };

    // Use setTimeout to avoid immediate close on the click that opened the menu
    setTimeout(() => {
      document.addEventListener('click', this.clickOutsideHandler!, true);
    }, 0);
  }

  toggleMenu() {
    this.isMenuOpen.update(v => {
      const newValue = !v;
      if (newValue) {
        // Add listener when menu opens
        this.addClickOutsideListener();
      } else {
        // Remove listener when menu closes
        this.removeClickOutsideListener();
      }
      return newValue;
    });
  }

  setSize(size: WidgetSize) {
    this.currentSize.set(size);
    this.isMenuOpen.set(false);
    this.removeClickOutsideListener();
  }

  formatToShort(value: number): string {
    // Indonesian formatting: Jt (Juta) for Million, M (Miliar) for Billion
    const billion = value / 1_000_000_000;
    if (billion >= 1) {
      return `${new Intl.NumberFormat('id-ID', { maximumFractionDigits: 1 }).format(billion)}M`; // Miliar
    }
    const million = value / 1_000_000;
    if (million >= 1) {
      return `${new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(million)}Jt`; // Juta
    }
    return value.toString();
  }

  get haircutWidth(): number {
    if (!this.data || this.data.haircutPercentage === undefined || this.data.haircutPercentage === null) {
      return 0;
    }
    return Math.min(Math.max(this.data.haircutPercentage, 0), 100);
  }
}
